// src/screens/PagoPendienteScreen.js
import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import { doc, getDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { 
  FaWhatsapp, 
  FaCopy, 
  FaCheck, 
  FaUniversity, 
  FaReceipt, 
  FaClock,
  FaSignOutAlt
} from "react-icons/fa";

const APP_PRIMARY = "#b03060";

// Catálogo inmutable de planes y sus precios oficiales
const CATALOGO_PLANES = Object.freeze({
  trial: { nombre: "Plan Semanal (7 días)", precio: 4990 },
  semanal: { nombre: "Plan Semanal (7 días)", precio: 4990 },
  mensual: { nombre: "Plan Mensual", precio: 29990 },
  semestral: { nombre: "Plan Semestral", precio: 139990 },
  anual: { nombre: "Plan Anual", precio: 199990 }
});

const resolverPlan = (identificador) => {
  if (!identificador) return CATALOGO_PLANES.trial;
  const key = String(identificador).toLowerCase().trim();
  
  if (CATALOGO_PLANES[key]) return CATALOGO_PLANES[key];
  if (key.includes("trial") || key.includes("7") || key.includes("seman")) return CATALOGO_PLANES.trial;
  if (key.includes("semestr")) return CATALOGO_PLANES.semestral;
  if (key.includes("anual") || key.includes("año")) return CATALOGO_PLANES.anual;
  if (key.includes("mensual") || key.includes("mes")) return CATALOGO_PLANES.mensual;

  return CATALOGO_PLANES.trial;
};

const PagoPendienteScreen = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState("Plan Semanal (7 días)");
  const [precio, setPrecio] = useState("$4.990");
  const [copiado, setCopiado] = useState(false);
  const [copiadoTodo, setCopiadoTodo] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [pagoAprobado, setPagoAprobado] = useState(false);
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");

  // Estado para los datos bancarios cargados de forma segura desde Firestore (V-03)
  const [datosTransferencia, setDatosTransferencia] = useState({
    banco: "Cargando...",
    tipoCuenta: "...",
    numeroCuenta: "...",
    titular: "...",
    rut: "...",
    email: "..."
  });

  const whatsappNumero = "56967876138";

  useEffect(() => {
    let unsubscribeDoc = null;

    // Cargar datos de pago protegidos desde Firestore (Colección 'configuracion', documento 'datos_pago')
    const cargarDatosPagoSeguros = async () => {
      try {
        const configRef = doc(db, "configuracion", "datos_pago");
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          setDatosTransferencia(configSnap.data());
        }
      } catch (err) {
        console.error("Error al obtener datos de transferencia seguros:", err);
      }
    };

    cargarDatosPagoSeguros();

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const userRef = doc(db, "usuarios", user.uid);
      unsubscribeDoc = onSnapshot(userRef, (docSnap) => {
        if (!docSnap.exists()) {
          navigate("/plans", { replace: true });
          return;
        }

        const data = docSnap.data();

        // Si ya está activo, redirigir automáticamente al home
        if (data.subscriptionStatus === "active" || data.estado === "activo") {
          setPagoAprobado(true);
          setTimeout(() => {
            navigate("/home", { replace: true });
          }, 1500);
          return;
        }

        setNombre(data.nombre || "");
        setApellido(data.apellido || "");

        const planFirestore = data.plan || data.planEntrenamiento || data.planId;
        const resolved = resolverPlan(planFirestore);
        setPlan(resolved.nombre);
        setPrecio("$" + resolved.precio.toLocaleString("es-CL"));

        if (data.subscriptionStatus === "payment_sent") {
          setConfirmado(true);
        }
      });
    });

    return () => {
      if (unsubscribeDoc) unsubscribeDoc();
      unsubscribeAuth();
    };
  }, [navigate]);

  const copiarCuenta = () => {
    navigator.clipboard.writeText(datosTransferencia.numeroCuenta);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  const copiarTodosLosDatos = () => {
    const textoCompleto = `Banco: ${datosTransferencia.banco}
Tipo de cuenta: ${datosTransferencia.tipoCuenta}
Número de cuenta: ${datosTransferencia.numeroCuenta}
Titular: ${datosTransferencia.titular}
RUT: ${datosTransferencia.rut}
Correo: ${datosTransferencia.email}
Monto a transferir: ${precio}`;

    navigator.clipboard.writeText(textoCompleto);
    setCopiadoTodo(true);
    setTimeout(() => setCopiadoTodo(false), 2500);
  };

  const abrirWhatsapp = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        await updateDoc(doc(db, "usuarios", user.uid), {
          subscriptionStatus: "payment_sent",
          paymentReportedAt: serverTimestamp(),
          planNombreReportado: plan,
          precioReportado: precio,
          updatedAt: serverTimestamp()
        });
        setConfirmado(true);
      } catch (error) {
        console.error("Error al reportar aviso de pago:", error);
      }
    }

    const mensajeWhatsApp = `¡Hola Mariana! Soy ${nombre} ${apellido}.\nAcabo de realizar la transferencia para el *${plan}* (${precio}).\nAdjunto mi comprobante de pago.`;
    const texto = encodeURIComponent(mensajeWhatsApp);

    window.open(`https://wa.me/${whatsappNumero}?text=${texto}`, "_blank");
  };

  const handleCerrarSesion = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Error cerrando sesión:", err);
    }
  };

  return (
    <div style={ui.wrapper}>
      <div style={ui.card}>
        {/* Caso 1: Pago aprobado y activo */}
        {pagoAprobado && (
          <div style={ui.stateBox}>
            <div style={{ ...ui.iconCircle, backgroundColor: "#dcfce7", color: "#16a34a" }}>
              <FaCheck size={32} />
            </div>
            <h2 style={{ ...ui.stateTitle, color: "#15803d" }}>¡Pago confirmado!</h2>
            <p style={ui.stateDesc}>
              Tu acceso a Mariana Fitness Trainer ha sido activado. Redirigiendo a tu panel...
            </p>
          </div>
        )}

        {/* Caso 2: Comprobante enviado, en espera de validación */}
        {confirmado && !pagoAprobado && (
          <div style={ui.stateBox}>
            <div style={{ ...ui.iconCircle, backgroundColor: "#fef3c7", color: "#d97706" }}>
              <FaClock size={32} />
            </div>
            <h2 style={{ ...ui.stateTitle, color: "#92400e" }}>
              ¡Aviso de transferencia enviado!
            </h2>
            <p style={ui.stateDesc}>
              Mariana validará tu comprobante y habilitará tu acceso de inmediato.
            </p>
            <div style={ui.alertSubtle}>
              ¿Necesitas reenviar el comprobante o consultar tus dudas? Abre el chat directo:
            </div>
            <button style={ui.btnWhatsappSecondary} onClick={abrirWhatsapp}>
              <FaWhatsapp size={18} /> Abrir WhatsApp nuevamente
            </button>
            <div style={{ marginTop: 24 }}>
              <button onClick={handleCerrarSesion} style={ui.logoutBtn}>
                <FaSignOutAlt size={13} /> Cerrar sesión
              </button>
            </div>
          </div>
        )}

        {/* Caso 3: Pendiente de pago normal */}
        {!confirmado && !pagoAprobado && (
          <>
            <div style={ui.header}>
              <span style={ui.badge}>Paso final</span>
              <h2 style={ui.title}>Tu cuenta está pendiente de activación</h2>
              <p style={ui.subtitle}>
                Transfiere el valor de tu suscripción y envía el comprobante para habilitar tus rutinas.
              </p>
            </div>

            {/* Tarjeta del Plan y Precio */}
            <div style={ui.planCard}>
              <div style={ui.planHeader}>
                <div style={ui.planIconBox}>
                  <FaReceipt size={16} color={APP_PRIMARY} />
                </div>
                <span style={ui.planLabel}>Resumen de suscripción</span>
              </div>
              <div style={ui.planDetailsRow}>
                <div>
                  <span style={ui.planFieldTag}>Plan Seleccionado</span>
                  <div style={ui.planName}>{plan}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={ui.planFieldTag}>Total a transferir</span>
                  <div style={ui.planPrice}>{precio}</div>
                </div>
              </div>
            </div>

            {/* Datos de transferencia bancaria */}
            <div style={ui.bankCard}>
              <div style={ui.bankHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaUniversity color={APP_PRIMARY} size={16} />
                  <span style={ui.bankTitle}>Datos para Transferencia</span>
                </div>
                <button
                  type="button"
                  onClick={copiarTodosLosDatos}
                  style={{
                    ...ui.btnCopyAll,
                    backgroundColor: copiadoTodo ? "#f0fdf4" : "#ffffff",
                    color: copiadoTodo ? "#15803d" : APP_PRIMARY,
                    borderColor: copiadoTodo ? "#86efac" : "#fbcfe8"
                  }}
                >
                  {copiadoTodo ? <FaCheck size={11} /> : <FaCopy size={11} />}
                  <span>{copiadoTodo ? "¡Datos copiados!" : "Copiar todos los datos"}</span>
                </button>
              </div>

              <div style={ui.bankGrid}>
                <div style={ui.bankRow}>
                  <span style={ui.bankKey}>Banco:</span>
                  <strong style={ui.bankVal}>{datosTransferencia.banco}</strong>
                </div>

                <div style={ui.bankRow}>
                  <span style={ui.bankKey}>Tipo de cuenta:</span>
                  <strong style={ui.bankVal}>{datosTransferencia.tipoCuenta}</strong>
                </div>

                <div style={ui.accountRow}>
                  <div>
                    <span style={ui.bankKey}>Número de cuenta:</span>
                    <div style={ui.accountNumber}>{datosTransferencia.numeroCuenta}</div>
                  </div>
                  <button 
                    type="button"
                    onClick={copiarCuenta} 
                    style={{
                      ...ui.btnCopy,
                      backgroundColor: copiado ? "#f0fdf4" : "#fdf2f8",
                      color: copiado ? "#15803d" : APP_PRIMARY,
                      borderColor: copiado ? "#86efac" : "#fbcfe8"
                    }}
                  >
                    {copiado ? <FaCheck size={12} /> : <FaCopy size={12} />}
                    <span>{copiado ? "¡Copiado!" : "Copiar número"}</span>
                  </button>
                </div>

                <div style={ui.bankRow}>
                  <span style={ui.bankKey}>Titular:</span>
                  <strong style={ui.bankVal}>{datosTransferencia.titular}</strong>
                </div>

                <div style={ui.bankRow}>
                  <span style={ui.bankKey}>RUT:</span>
                  <strong style={ui.bankVal}>{datosTransferencia.rut}</strong>
                </div>

                <div style={ui.bankRow}>
                  <span style={ui.bankKey}>Correo de confirmación:</span>
                  <strong style={ui.bankVal}>{datosTransferencia.email}</strong>
                </div>
              </div>
            </div>

            {/* Botón WhatsApp */}
            <button 
              type="button"
              style={ui.btnWhatsapp} 
              onClick={abrirWhatsapp}
            >
              <FaWhatsapp size={20} />
              <span>Enviar comprobante por WhatsApp</span>
            </button>

            <div style={{ textAlign: "center", marginTop: 18 }}>
              <button onClick={handleCerrarSesion} style={ui.logoutBtn}>
                <FaSignOutAlt size={13} /> Cerrar sesión
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PagoPendienteScreen;

/* Estilos */
const ui = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f8fafc",
    padding: "24px 16px",
    fontFamily: "'Poppins', sans-serif",
    boxSizing: "border-box"
  },
  card: {
    width: "100%",
    maxWidth: "480px",
    background: "#ffffff",
    borderRadius: "24px",
    padding: "36px 28px",
    boxShadow: "0 10px 35px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f1f5f9",
    boxSizing: "border-box"
  },
  header: {
    textAlign: "center",
    marginBottom: "24px"
  },
  badge: {
    display: "inline-block",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: APP_PRIMARY,
    background: "#fdf2f8",
    padding: "4px 14px",
    borderRadius: "20px",
    marginBottom: "10px"
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 6px 0",
    letterSpacing: "-0.3px",
    lineHeight: 1.3
  },
  subtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
    lineHeight: 1.5
  },
  planCard: {
    borderRadius: "16px",
    padding: "16px 18px",
    marginBottom: "18px",
    border: "1.5px solid #fbcfe8",
    background: "linear-gradient(to bottom right, #ffffff, #fffdfd)",
    boxShadow: "0 4px 14px rgba(176, 48, 96, 0.05)"
  },
  planHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px"
  },
  planIconBox: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    backgroundColor: "#fdf2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  planLabel: {
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: APP_PRIMARY
  },
  planDetailsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end"
  },
  planFieldTag: {
    fontSize: "11px",
    color: "#94a3b8",
    display: "block",
    marginBottom: "2px"
  },
  planName: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b"
  },
  planPrice: {
    fontSize: "22px",
    fontWeight: "800",
    color: APP_PRIMARY,
    letterSpacing: "-0.5px"
  },
  bankCard: {
    backgroundColor: "#fafafa",
    borderRadius: "16px",
    padding: "18px",
    border: "1px solid #f1f5f9",
    marginBottom: "24px"
  },
  bankHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px",
    paddingBottom: "8px",
    borderBottom: "1px solid #f1f5f9"
  },
  bankTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155"
  },
  btnCopyAll: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "5px 10px",
    fontSize: "11px",
    fontWeight: "600",
    borderRadius: "6px",
    border: "1px solid",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none"
  },
  bankGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "9px"
  },
  bankRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "13px"
  },
  bankKey: {
    color: "#64748b"
  },
  bankVal: {
    color: "#1e293b",
    fontWeight: "600"
  },
  accountRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    margin: "4px 0"
  },
  accountNumber: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "0.5px"
  },
  btnCopy: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
    borderRadius: "8px",
    border: "1px solid",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none"
  },
  btnWhatsapp: {
    width: "100%",
    padding: "14px 20px",
    backgroundColor: "#25D366",
    color: "#ffffff",
    border: "none",
    borderRadius: "14px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    boxShadow: "0 6px 18px rgba(37, 211, 102, 0.28)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    outline: "none"
  },
  btnWhatsappSecondary: {
    marginTop: "16px",
    padding: "10px 18px",
    backgroundColor: "#ffffff",
    color: "#25D366",
    border: "1.5px solid #25D366",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px"
  },
  logoutBtn: {
    background: "none",
    border: "none",
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  },
  stateBox: {
    padding: "20px 10px",
    textAlign: "center"
  },
  iconCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px"
  },
  stateTitle: {
    fontSize: "20px",
    fontWeight: "700",
    margin: "0 0 8px 0"
  },
  stateDesc: {
    fontSize: "14px",
    color: "#64748b",
    lineHeight: 1.5,
    margin: 0
  },
  alertSubtle: {
    marginTop: "16px",
    fontSize: "12px",
    color: "#94a3b8",
    lineHeight: 1.4
  }
};